import { useMemo, useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { ModalShell } from './ModalShell'
import { IngredientEditModal } from './IngredientEditModal'
import { fmtCanon } from '../units'
import type { ReconcileRequired, ReconcilePayload, IngredientRow, Me } from '../types'

type Props = {
  open: boolean
  envelope: ReconcileRequired | null
  me: Me
  onCancel: () => void
  onConfirm: (payload: ReconcilePayload) => Promise<void> | void
}

// Cook Reconcile dialog.
// Shown when marking a meal "cooked" hits the pantry preflight: stock is
// insufficient for at least one ingredient, or the recipe references
// ingredients the user never recorded a lot for. The user can correct the
// quantities for tracked lots, or fill in (or leave blank) the untracked ones.
// "Blank" untracked ingredients stay untracked — no pantry_item is created
// and the cook deduction silently records the shortfall in the ledger.
export function CookReconcileModal({ open, envelope, me, onCancel, onConfirm }: Props) {
  const insufficient = envelope?.insufficient ?? []
  const missing = envelope?.missing ?? []
  const prefs = { mass: me.mass_units, volume: me.volume_units }

  // For each lot in "insufficient", track the user-asserted true quantity.
  const initialLots = useMemo(() => {
    const m: Record<number, { quantity: string; unit: string }> = {}
    for (const i of insufficient) {
      for (const l of i.lots ?? []) {
        m[l.id] = { quantity: String(l.quantity), unit: l.unit || '' }
      }
    }
    return m
  }, [envelope])
  const initialMissing = useMemo(() => {
    const m: Record<number, { quantity: string; unit: string }> = {}
    for (const x of missing) {
      m[x.ingredient_id] = { quantity: '', unit: x.suggested_unit || '' }
    }
    return m
  }, [envelope])

  const [lots, setLots] = useState(initialLots)
  const [miss, setMiss] = useState(initialMissing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [viewingIng, setViewingIng] = useState<IngredientRow | null>(null)
  const [viewKey, setViewKey] = useState(0)

  // Open the Edit ingredient modal for a row. Fetch the canonical row by id
  // and adopt system rows on the way in so the editor opens editable.
  const openIngredientView = async (ingredientID: number) => {
    setError(null)
    try {
      let row = await api.getIngredient(ingredientID)
      if (!row.owned) {
        row = await api.adoptIngredient(row.id)
      }
      setViewingIng(row)
      setViewKey(k => k + 1)
    } catch (e) {
      setError(String(e))
    }
  }

  const confirm = async () => {
    setError(null)
    setSaving(true)
    try {
      const payload: ReconcilePayload = {
        set_pantry_items: Object.entries(lots).flatMap(([id, v]) => {
          const q = Number(v.quantity)
          if (!isFinite(q) || q < 0 || !v.unit) return []
          return [{ pantry_item_id: Number(id), quantity: q, unit: v.unit }]
        }),
        create_pantry_items: Object.entries(miss).flatMap(([id, v]) => {
          const q = Number(v.quantity)
          if (!v.quantity || !isFinite(q) || q <= 0 || !v.unit) return []
          return [{ ingredient_id: Number(id), quantity: q, unit: v.unit }]
        }),
      }
      await onConfirm(payload)
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalShell
      open={open && !!envelope}
      onClose={onCancel}
      zIndex={80}
      panelStyle={{
        width: 'min(520px, 100%)', maxHeight: '90dvh', overflowY: 'auto',
        padding: '18px 20px', gap: 14,
      }}
    >
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>Before cooking</div>
          <div style={{
            fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
            color: theme.ink, marginTop: 2,
          }}>Reconcile pantry</div>
          <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.inkSoft, marginTop: 6 }}>
            Your pantry doesn't have enough of some ingredients. Update the true
            amounts on hand below, or leave the "Not tracked yet" rows blank to
            cook without recording stock for them.
          </div>
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
            fontFamily: theme.sans, fontSize: 12,
          }}>{error}</div>
        )}

        {insufficient.length > 0 && (
          <div>
            <SectionLabel>On hand (verify amounts)</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
              {insufficient.map(i => (
                <div key={i.ingredient_id} style={rowGroup}>
                  <div style={rowHeader}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                      <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink }}>{i.name}</div>
                      <button onClick={() => openIngredientView(i.ingredient_id)} style={btnLink}>View</button>
                    </div>
                    <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted }}>
                      need {fmtCanon(i.required_canonical, i.category, prefs)} ·
                      have {fmtCanon(i.on_hand_canonical, i.category, prefs)}
                    </div>
                  </div>
                  {(i.lots ?? []).map(l => {
                    const v = lots[l.id] || { quantity: String(l.quantity), unit: l.unit || '' }
                    return (
                      <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="number" inputMode="decimal" min="0" step="any"
                          value={v.quantity}
                          onChange={e => setLots(m => ({ ...m, [l.id]: { ...v, quantity: e.target.value } }))}
                          style={{ ...inputStyle, flex: 1 }}/>
                        <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.inkSoft, width: 60 }}>
                          {v.unit}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {missing.length > 0 && (
          <div>
            <SectionLabel>Not tracked yet (optional)</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
              {missing.map(m => {
                const v = miss[m.ingredient_id] || { quantity: '', unit: m.suggested_unit || '' }
                return (
                  <div key={m.ingredient_id} style={rowGroup}>
                    <div style={rowHeader}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                        <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink }}>{m.name}</div>
                        <button onClick={() => openIngredientView(m.ingredient_id)} style={btnLink}>View</button>
                      </div>
                      <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted }}>
                        recipe needs {fmtCanon(m.required_canonical, m.category, prefs)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="number" inputMode="decimal" min="0" step="any"
                        placeholder="leave blank to skip tracking"
                        value={v.quantity}
                        onChange={e => setMiss(s => ({ ...s, [m.ingredient_id]: { ...v, quantity: e.target.value } }))}
                        style={{ ...inputStyle, flex: 1 }}/>
                      <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.inkSoft, width: 60 }}>
                        {v.unit}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
          <button onClick={confirm} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Cooking…' : 'Confirm and cook'}
          </button>
        </div>

        <IngredientEditModal
          key={viewKey}
          open={viewingIng !== null}
          ingredient={viewingIng}
          zIndex={90}
          onCancel={() => setViewingIng(null)}
          onSaved={() => setViewingIng(null)}
        />
    </ModalShell>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.2, fontWeight: 600,
      textTransform: 'uppercase', color: theme.muted,
    }}>{children}</div>
  )
}

const rowGroup: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  padding: '10px 12px', borderRadius: theme.radius,
  background: theme.bg, border: `1px solid ${theme.line}`,
}
const rowHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
}
const inputStyle: React.CSSProperties = {
  all: 'unset', boxSizing: 'border-box',
  padding: '7px 10px', borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`, background: theme.surface,
  fontFamily: theme.sans, fontSize: 13, color: theme.ink,
}
const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}
const btnLink: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
  textTransform: 'uppercase', color: theme.accentInk,
  padding: '2px 6px', borderRadius: theme.radius,
}
const btnPrimary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface, background: theme.ink,
}
